import React, { useState, useEffect } from 'react';
import { FiPrinter, FiInfo, FiArrowLeft } from 'react-icons/fi';
import config from '../../config/config';
import { useLocation, useNavigate } from 'react-router-dom';

export function WeightPrint() {
  const location = useLocation();
  const navigate = useNavigate();
  const { category: passedCat, cat_name, subCat, type: passedType, day: passedDay } = location.state || {};

  const [weightData, setWeightData] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchWeightReport = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const year = 2026;

      const queryParams = new URLSearchParams({
        year: year.toString(),
        category: passedCat || '',
        type: passedType || '',
        day: passedDay || ''
      }).toString();

      const response = await fetch(
        `${config.baseUrl}/api/reports/Weight-Print?${queryParams}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) throw new Error('Failed to fetch data');
      const data = await response.json();
      setWeightData(data);
    } catch (error) {
      console.error('Error fetching print data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWeightReport();
  }, [passedCat, passedType, passedDay]);

  // FIXED: Totals Calculation for conditional columns
  const totalShort = weightData.reduce((sum, item) => sum + Math.abs(parseFloat(item.wsh) || 0), 0);
  const totalPenalty = weightData.reduce((sum, item) => sum + (parseFloat(item.penalty) || 0), 0);

  return (
    <div className="min-h-screen bg-slate-50 p-3 md:p-8">
      {/* Print Specific CSS with Landscape Orientation */}
      <style dangerouslySetInnerHTML={{
        __html: `
        @media print {
          @page { 
            size: A4 landscape; 
            margin: 10mm; 
          }
          html, body { 
            background: white !important; 
            margin: 0 !important; 
            padding: 0 !important; 
          }
          .no-print { display: none !important; }
          #printable-area { width: 100% !important; margin: 0 !important; }
          
          .print-card { 
            box-shadow: none !important; 
            border: none !important; 
            border-radius: 0 !important; 
          }

          table { 
            width: 100% !important; 
            border-collapse: collapse !important; 
            table-layout: auto !important;
            border: 1pt solid black !important;
          }

          th, td { 
            border: 0.5pt solid black !important; 
            padding: 6px 3px !important; 
            font-size: 8.5pt !important; 
            color: black !important;
            text-align: center !important;
          }

          th { 
            background-color: #1e293b !important; 
            color: white !important; 
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            text-transform: uppercase;
          }

          .footer-row td { 
            background-color: #f1f5f9 !important; 
            font-weight: bold !important; 
            border-top: 1.5pt solid black !important;
            -webkit-print-color-adjust: exact;
          }
        }
      `}} />

      <div id="printable-area" className="max-w-7xl mx-auto">
        {/* Top Header Section */}
        <div className="mb-6 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate(-1)} className="no-print p-2 bg-white border rounded-lg hover:bg-slate-50 transition-all">
                <FiArrowLeft size={18} />
              </button>
              <h1 className="text-2xl md:text-3xl font-black text-indigo-800 tracking-tight italic">
                Weight Report Analysis
              </h1>
            </div>

            {/* Meta Information Cards */}
            <div className='grid grid-cols-2 md:grid-cols-3 lg:flex lg:flex-wrap items-center gap-3 md:gap-6 py-4 px-5 mt-4 bg-white border border-slate-200 rounded-2xl shadow-sm'>
              <div className="flex flex-col">
                <span className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Category</span>
                <span className="text-sm font-black text-slate-800">{cat_name || 'N/A'}</span>
              </div>
              <div className="hidden lg:block h-8 w-[1px] bg-slate-200 print:hidden"></div>

              <div className="flex flex-col">
                <span className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Type</span>
                <span className="text-sm font-black text-slate-800">{subCat || 'N/A'}</span>
              </div>
              <div className="hidden lg:block h-8 w-[1px] bg-slate-200 print:hidden"></div>

              <div className="flex flex-col">
                <span className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Day</span>
                <span className="text-sm font-black text-slate-800">{passedDay || 'N/A'}</span>
              </div>

              {/* Hidden in Print */}
              <div className="hidden lg:block h-8 w-[1px] bg-slate-200 no-print"></div>
              <div className="flex flex-col no-print">
                <span className="text-[10px] uppercase tracking-widest text-indigo-500 font-bold">Limit</span>
                <span className="text-sm font-black text-indigo-700">{weightData[0]?.weight_limit || '0'} kg</span>
              </div>

              <div className="hidden lg:block h-8 w-[1px] bg-slate-200 no-print"></div>
              <div className="flex flex-col no-print">
                <span className="text-[10px] uppercase tracking-widest text-red-500 font-bold">Rate/kg</span>
                <span className="text-sm font-black text-red-700">{weightData[0]?.deduction_rate || '0'}</span>
              </div>
            </div>
          </div>

          <button
            onClick={() => window.print()}
            className="no-print w-full lg:w-auto flex items-center justify-center gap-2 bg-indigo-800 text-white px-8 py-4 rounded-2xl hover:bg-indigo-900 shadow-xl font-bold transition-all active:scale-95"
          >
            <FiPrinter size={20} /> Print Report
          </button>
        </div>

        {/* Main Table Container */}
        <div className="print-card bg-white rounded-xl shadow-xl border border-slate-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-indigo-800 text-white text-[10px] uppercase tracking-widest text-center">
                  <th className="py-4 px-2 w-[50px]">S.No</th>
                  <th className="py-4 px-2">Animal No.</th>
                  <th className="py-4 px-2">Weight A</th>
                  <th className="py-4 px-2">Weight B</th>
                  <th className="py-4 px-2">Weight C</th>
                  <th className="py-4 px-2">Weight D</th>
                  <th className="py-4 px-2 ">Total</th>
                  <th className={`py-4 px-2 ${totalShort <= 0 ? 'print:hidden' : ''}`}>Short</th>
                  <th className={`py-4 px-2 ${totalPenalty <= 0 ? 'print:hidden' : ''}`}>Deduction</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="9" className="py-20 text-center">
                      <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
                    </td>
                  </tr>
                ) : weightData.length > 0 ? (
                  <>
                    {weightData.map((item, index) => (
                      <tr key={index} className="hover:bg-slate-50 transition-colors">
                        <td className="py-3 text-slate-400 font-bold italic">{index + 1}</td>
                        <td className="py-3 font-black text-slate-800 uppercase">{item.animal_number}</td>
                        <td className="py-3">{item.wa || " "}</td>
                        <td className="py-3">{item.wb || " "}</td>
                        <td className="py-3">{item.wc || " "}</td>
                        <td className="py-3">{item.wd || " "}</td>
                        <td className={`py-3 font-black text-indigo-900 bg-indigo-50/20 ${parseFloat(item.wt) <= 0 ? 'print:hidden' : ''}`}>
                          {parseFloat(item.wt) > 0 ? item.wt : ""}
                        </td>
                        <td className={`py-3 text-red-600 font-bold text-center ${totalShort <= 0 ? 'print:hidden' : ''}`}>
                          {/* Agar short weight 0 nahi hai, toh uska absolute value dikhao */}
                          {parseFloat(item.wsh) !== 0 ? `${Math.abs(item.wsh)} ` : ""}
                        </td>
                        <td className={`py-3 text-red-700 font-black text-center ${totalPenalty <= 0 ? 'print:hidden' : ''}`}>
                          {parseFloat(item.penalty) > 0 ? `PKR ${item.penalty}` : ""}
                        </td>
                      </tr>
                    ))}
                    {/* Summary Footer Row */}
                    <tr className="footer-row text-center ">
                      <td colSpan="6" className={`py-4  text-right font-black ${totalShort <= 0 ? 'print:hidden' : ''}`}>
                        Total Summary
                      </td>
                      <td className="">
                      </td>
                      <td className={`py-4 text-red-600 ${totalShort <= 0 ? 'print:hidden' : ''}`}>
                        {totalShort.toFixed(2)} kg
                      </td>
                      <td className={`py-4 text-emerald-700 ${totalPenalty <= 0 ? 'print:hidden' : ''}`}>
                        PKR {totalPenalty.toLocaleString()}
                      </td>
                    </tr>
                  </>
                ) : (
                  <tr>
                    <td colSpan="9" className="py-20 text-center text-slate-400 font-bold uppercase tracking-widest">
                      <FiInfo className="mx-auto mb-2" size={30} />
                      No Animals Found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}